import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDate, fromISODate } from '@/lib/utils/dateHelpers';
import type { QuantityProblem } from '@/types/api';

interface QuantityProblemsDialogProps {
  problems: QuantityProblem[] | null;
  onClose: () => void;
}

const formatNumber = (value: string) =>
  Number(value).toLocaleString('en-US', { maximumFractionDigits: 4 });

const REASON_LABELS: Record<QuantityProblem['reason'], string> = {
  PER_SERVING: 'above 10,000 per serving',
  WEEKLY_TOTAL: 'weekly total above 999,999.9999',
};

const STAPLE_REASON = 'purchase after order-increment rounding above 999,999.9999';

export function QuantityProblemsDialog({ problems, onClose }: QuantityProblemsDialogProps) {
  const hasStaple = (problems ?? []).some((problem) => problem.recipeId === null);
  return (
    <Dialog open={problems !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-4xl" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{hasStaple ? 'Fix these quantities first' : 'Fix these recipe quantities first'}</DialogTitle>
          <DialogDescription>
            {hasStaple
              ? 'The amounts below are too large to fit in a procurement list.'
              : 'The amounts below are too large to be right or to fit in a procurement list. They are almost certainly entered in the wrong unit.'}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 pr-3 font-medium">Recipe</th>
                <th className="py-2 pr-3 font-medium">Ingredient</th>
                <th className="py-2 pr-3 font-medium">Per serving</th>
                <th className="py-2 pr-3 font-medium">This week</th>
                <th className="py-2 pr-3 font-medium">Why</th>
                <th className="py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {(problems ?? []).map((problem, index) => (
                <tr
                  key={`${problem.ingredientId}-${problem.recipeId ?? 'staple'}-${problem.menuDate ?? index}-${index}`}
                  className="border-b align-top"
                >
                  <td className="py-2 pr-3">
                    {problem.recipeName}
                    {problem.menuDate && (
                      <span className="text-muted-foreground">
                        {' '}({formatDate(fromISODate(problem.menuDate), 'EEE d MMM')})
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3">{problem.ingredientName}</td>
                  <td className="py-2 pr-3">
                    {problem.recipeId === null && 'target '}
                    {formatNumber(problem.quantityPerLarge)} {problem.unit}
                  </td>
                  <td className="py-2 pr-3">{formatNumber(problem.weeklyQuantity)} {problem.unit}</td>
                  <td className="py-2 pr-3">
                    {problem.recipeId === null ? STAPLE_REASON : REASON_LABELS[problem.reason]}
                  </td>
                  <td className="py-2">
                    {problem.recipeId !== null ? (
                      <a
                        className="font-medium text-primary underline"
                        href={`/admin/library/recipes?recipe=${problem.recipeId}`}
                      >
                        Open recipe
                      </a>
                    ) : (
                      <a className="font-medium text-primary underline" href="/admin/library/ingredients">
                        Open ingredient
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
